// look for [data-autocomplete]. currently only works for input elements (meant for text inputs).
//  the value of data-autocomplete is the type of autocomplete.
//  autocomplete types determine the API URL to fetch data from (e.g. keyword, location, and occupation)
// [data-autocomplete-length] sets the minimum length of the element value before kicking off the API call.
//   without this value, we use the default length of 2 characters.
// [data-autocomplete-onfocus] (defaults to true). If true, loads autocomplete options on focus. If false, focusing on the element alone does not load the autocomplete options.
// NOTE: This component does not automatically bind elements. It depends on a promise in the session.
import { getRandomId, debounce, uiBreakpoints } from '../utilities';
import { AutocompleteListState, AutocompleteResult, ACClickEventDetails, AutocompleteResults } from '../interfaces';
import { getUsajConfig } from '../session/sessionmain';

const ValidAutocompleteTypes = [
    'keyword', 'location', 'occupation', 'language'
];
const searchIconName = 'search';
const locationIconName = 'location_on';
const closeIconHtml = '<svg class="w-4 h-4 fill-black mr-1 pointer-events-none" role="presentation"><use href="/img/icons/sprite.svg#close"></use></svg>';
const acElementIdPrefix = 'autocomplete';
const acContainerIdPrefix = 'autocompletecontainer';
const acItemIdPrefix = 'autocompleteItem';
const acGroupIdPrefix = 'autocompletegroup';
const headerInputIdPrefix = 'autocompleteHeaderInput';
const removeRecentIdPrefix = 'removeRecent';
const acBackdropIdPrefix = 'acbackdrop';
const loadingIndicatorIdPrefix = 'indicator';
const MinLength = 2;
const MaxResults = 10;
let DataHome = '';
let BaseUrl = '';
let autocompleteLists: AutocompleteListState[] = [];
let autocompleteViewportListenersBound = false;
let autocompleteUIElements: { elem: HTMLInputElement; autocomplete: HTMLElement; elemContainer: HTMLElement; }[] = [];
const passiveEventListenerOptions = { passive: true };
const interactions = new WeakMap<HTMLInputElement, { active: boolean; version: number; restoringFocus: boolean }>();

function getInteraction(elem: HTMLInputElement) {
    let state = interactions.get(elem);
    if (!state) {
        state = {
            active: false,
            version: 0,
            restoringFocus: false
        };
        interactions.set(elem, state);
    }
    return state;
}

function beginInteraction(elem: HTMLInputElement, id: string) {
    const state = getInteraction(elem);
    if (state.restoringFocus) { return false; }

    autocompleteUIElements.forEach(item => {
        if (item.elem !== elem && getInteraction(item.elem).active) {
            setAutocompleteDisplay(item.elem, item.elemContainer.id.substring(acContainerIdPrefix.length), false);
        }
    });
    state.active = true;
    state.version++;
    if (window.innerWidth <= uiBreakpoints.extraSmall) {
        setAutocompleteDisplay(elem, id, true);
        mobileHeaderInputFocus(elem, id);
    }
    return true;
}

function restoreAutocompleteFocus(elem: HTMLInputElement) {
    const state = getInteraction(elem);
    state.restoringFocus = true;
    try {
        elem.focus({ preventScroll: true });
    }
    finally {
        state.restoringFocus = false;
    }
}

function updateAutocompleteUIElements() {
    autocompleteUIElements = autocompleteUIElements.filter(item => item.elem.isConnected && item.autocomplete.isConnected && item.elemContainer.isConnected);

    autocompleteUIElements.forEach(item => {
        resizeAutocomplete(item.elem, item.autocomplete);
        setMobileAutocompleteViewportPosition(item.elemContainer);
    });
}

function bindAutocompleteViewportListeners() {
    if (autocompleteViewportListenersBound) {
        return;
    }

    addEventListener('resize', updateAutocompleteUIElements);
    window.visualViewport?.addEventListener('resize', updateAutocompleteUIElements, passiveEventListenerOptions);
    window.visualViewport?.addEventListener('scroll', updateAutocompleteUIElements, passiveEventListenerOptions);
    autocompleteViewportListenersBound = true;
}

function autocompleteBlur(elem: HTMLInputElement, id: string) {
    const version = getInteraction(elem).version;
    setTimeout(() => {
        if (version !== getInteraction(elem).version || isAutocompleteInteractionActive(elem, id)) {
            return;
        }

        setAutocompleteDisplay(elem, id, false);
    }, 500);
}

function autocompleteFocus(elem: HTMLInputElement, id: string) {
    if (!beginInteraction(elem, id)) {
        return;
    }

    const acElem = document.querySelector<HTMLElement>(`#${acElementIdPrefix}${id}`);
    if (acElem?.querySelector('[role="option"]')) {
        setAutocompleteDisplay(elem, id, true);
    }
}

async function acSearchFocus(elem: HTMLInputElement, id: string) {
    if (!beginInteraction(elem, id) || elem.value.trim().length > 0) {
        return;
    }

    const acType = elem.getAttribute('data-autocomplete')?.toLowerCase();
    if (!acType || (acType !== 'keyword' && acType !== 'location')) {
        return;
    }

    const version = getInteraction(elem).version;
    const suggestions = getRecentSearches(acType) ?? await getPopularSearches(acType);
    if (suggestions && isCurrentRequest(elem, id, version)) {
        showAutocompleteResults(elem, suggestions, id, acType);
    }
}

function mobileHeaderInputFocus(elem: HTMLInputElement, id: string) {
    const mobileHeaderInput = document.querySelector<HTMLInputElement>(`#${headerInputIdPrefix}${id}`);
    if (mobileHeaderInput && window.innerWidth <= uiBreakpoints.extraSmall) {
        mobileHeaderInput.value = elem.value;
        mobileHeaderInput.focus({ preventScroll: true });
    }
}

function getVisualViewportBottomOffset() {
    if (!window.visualViewport) {
        return 0;
    }

    return Math.max(0, window.innerHeight - (window.visualViewport.height + window.visualViewport.offsetTop));
}

function setMobileAutocompleteViewportPosition(elemContainer: HTMLElement) {
    if (window.innerWidth > uiBreakpoints.extraSmall) {
        elemContainer.style.removeProperty('bottom');
        elemContainer.style.removeProperty('max-height');
        return;
    }

    const viewportHeight = window.visualViewport?.height;
    elemContainer.style.bottom = `${getVisualViewportBottomOffset()}px`;

    if (viewportHeight) {
        elemContainer.style.maxHeight = `${viewportHeight}px`;
    }
    else {
        elemContainer.style.removeProperty('max-height');
    }
}

export async function getPopularSearches(acType: "keyword" | "location", dataHome?: string): Promise<AutocompleteResults> {
    const baseUrl = dataHome ?? (typeof DataHome !== 'undefined' ? DataHome : '');
    return await fetch(`${baseUrl}api/autocomplete/${acType}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'force-cache',
        headers: { "Content-Type": "application/json" }
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Response returned ${response.status}`);
            }
            return response.json();
        })
        .catch(err => console.debug('Error', err));
}

export function getRecentSearches(type: "keyword" | "location" | null = null): AutocompleteResults | null {
    const recentSearches = <AutocompleteResults>JSON.parse(localStorage.getItem('RecentSearches') || '{ "Recent": [] }');
    let recentSearchesArray = type ? recentSearches['Recent'].filter(s => s.Type === type) : recentSearches['Recent'];

    return recentSearchesArray.length > 0 ? { 'Recent': recentSearchesArray } : null;
}

function setRecentSearchesInStorage(recentSearches: AutocompleteResults) {
    localStorage.setItem('RecentSearches', JSON.stringify(recentSearches));
}

function removeRecentSearch(name: string, type: string) {
    const recentSearches = getRecentSearches();
    if (!recentSearches) {
        return;
    }
    localStorage.setItem('RecentSearches', JSON.stringify({ "Recent": recentSearches['Recent'].filter(s => s.Name.trim().toLowerCase() !== name.trim().toLowerCase() && s.Type === type) }));
}

export function addRecentSearch(type: "keyword" | "location", name: string) {
    const recentSearches = getRecentSearches() ?? { 'Recent': [] };

    let typeRecentSearches = recentSearches['Recent'].filter(s => s.Type === type);
    const normalizedName = name.trim().toLowerCase();
    typeRecentSearches = typeRecentSearches.filter(s => s.Name.trim().toLowerCase() !== normalizedName);

    const otherTypeRecentSearches = recentSearches['Recent'].filter(s => s.Type !== type);

    typeRecentSearches.unshift({
        Type: type,
        Name: name,
        Code: '',
        ParentName: null,
        Acronym: '',
        SearchText: ''
    });
    typeRecentSearches = typeRecentSearches.length > MaxResults ? typeRecentSearches.slice(0, MaxResults) : typeRecentSearches;
    setRecentSearchesInStorage({ 'Recent': [...typeRecentSearches, ...otherTypeRecentSearches] });
}

function isCurrentRequest(elem: HTMLInputElement, id: string, version: number) {
    const state = getInteraction(elem);
    return state.active && state.version === version && isAutocompleteInteractionActive(elem, id);
}

function showAutocompleteResults(elem: HTMLInputElement, data: AutocompleteResults | null, id: string, acType: string) {
    populateAutocompleteList(elem, data, id, acType);
    if (window.innerWidth > uiBreakpoints.extraSmall) {
        const hasResults = !!document.querySelector(`#${acElementIdPrefix}${id} [role="option"]`);
        elem.setAttribute('aria-expanded', hasResults.toString());
        document.querySelector(`#${acContainerIdPrefix}${id}`)?.setAttribute('data-expanded', hasResults.toString());
    }
}

function autocompleteInput(evt: Event, elem: HTMLInputElement, id: string, version: number) {
    if (!isCurrentRequest(elem, id, version)) {
        return;
    }

    const acType = elem.getAttribute('data-autocomplete')?.toLowerCase();
    const mobileHeaderInput = document.querySelector<HTMLInputElement>(`#${headerInputIdPrefix}${id}`);

    if (acType === undefined || ValidAutocompleteTypes.indexOf(acType) < 0) {
        return;
    }

    if (mobileHeaderInput && evt.target === elem) {
        mobileHeaderInput.value = elem.value;
    }
    else if (mobileHeaderInput && evt.target === mobileHeaderInput) {
        elem.value = mobileHeaderInput.value;
    }

    if (elem.value.length < Number.parseInt(elem.getAttribute('data-autocomplete-length') || MinLength.toString())) {
        if (mobileHeaderInput) {
            mobileHeaderInput.value = elem.value;
        }
        showAutocompleteResults(elem, null, id, acType);
        displayLoadingIndicator(elem, id, false);
        return;
    }
    displayLoadingIndicator(elem, id);
    fetch(`${DataHome}api/autocomplete/${acType}?term=${encodeURIComponent(elem.value)}&size=${MaxResults}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'force-cache',
        headers: { "Content-Type": "application/json" }
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Response returned ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            if (isCurrentRequest(elem, id, version)) {
                showAutocompleteResults(elem, data, id, acType);
            }
        })
        .catch(err => console.debug('Error', err))
        .finally(() => {
            if (getInteraction(elem).version === version) {
                displayLoadingIndicator(elem, id, false);
            }
        });
}

function autocompleteKeyup(evt: KeyboardEvent, elem: HTMLInputElement, id: string, acevent: ACClickEventDetails | null = null) {
    const theKey = evt.code;

    if (document.querySelector<HTMLElement>(`#${acElementIdPrefix}${id}`) === null) {
        return;
    }
    if (theKey === 'ArrowUp' || theKey === 'ArrowDown') {
        evt.preventDefault();
        handleArrowKeypress(elem, id, theKey);
    } else if (theKey === 'Enter' || (theKey === 'Space' && acevent)) {
        evt.preventDefault();
        setAutocompleteDisplay(elem, id, false);
        restoreAutocompleteFocus(elem);
        if (acevent) {
            document.dispatchEvent(new CustomEvent("acclick", { detail: acevent }));
        }
    } else if (theKey === 'Escape') {
        evt.preventDefault();
        setAutocompleteDisplay(elem, id, false);
        restoreAutocompleteFocus(elem);
    }
}

function handleArrowKeypress(elem: HTMLElement, id: string, theKey: string) {
    const activeId = document.activeElement?.id;
    const listState = autocompleteLists.find(i => i.id === id);
    if (listState === undefined || listState.length === 0) {
        return;
    }

    if (document.activeElement === elem || document.activeElement?.id === `${headerInputIdPrefix}${id}`) {
        document.querySelector<HTMLElement>(`#${acItemIdPrefix}${id}-1`)?.focus();
        return;
    }

    let currentElemId = parseInt(activeId?.substring(activeId.indexOf('-')+1) || "0");
    let nextElemId = 0;
    if (theKey === 'ArrowUp') {
        nextElemId = (currentElemId < 2) ? listState.length : currentElemId - 1;
    }
    if (theKey === 'ArrowDown') {
        nextElemId = (currentElemId >= listState.length) ? 1 : currentElemId + 1;
    }
    const selectedElem = document.querySelector<HTMLElement>(`#${acItemIdPrefix}${id}-${nextElemId}`);
    selectedElem?.focus();
    selectedElem?.setAttribute('aria-selected', 'true');
    if (currentElemId > 0) {
        document.querySelector<HTMLElement>(`#${acItemIdPrefix}${id}-${currentElemId}`)?.setAttribute('aria-selected', 'false');
    }
}

function displayLoadingIndicator(elem: HTMLInputElement, id: string, isDisplayed: boolean = true) {
    let indicator = document.querySelector<HTMLDivElement>(`#${loadingIndicatorIdPrefix}${id}`);

    if (indicator === null && isDisplayed) {
        indicator = document.createElement('div');
        const indicatorImage = document.createElement('img');

        indicator.id = `${loadingIndicatorIdPrefix}${id}`;
        indicator.className = "absolute p-0 m-0";
        indicatorImage.src = '/img/icons/loader.gif';
        indicatorImage.alt = 'Loading. Please wait.';
        indicatorImage.className = "w-6 h-6";
        indicator.appendChild(indicatorImage);
        document.body.appendChild(indicator);
    }
    if (isDisplayed && indicator !== null) {
        indicator.classList.remove('hidden');
        indicator.style.top = (elem.getBoundingClientRect().top + 10).toString() + "px";
        indicator.style.left = (elem.getBoundingClientRect().right - 30).toString() + "px";
    }
    else if (indicator !== null) {
        indicator.classList.add('hidden');
    }
}

function buildAutocompleteUI(elem: HTMLInputElement, id: string, includeMobileHeader: boolean = true) {
    const group = document.createElement('div');
    const elemContainer = document.createElement('div');
    const autocompleteId = acElementIdPrefix + id;
    const autocomplete = document.createElement('ul');
    const assistiveElement = document.createElement('div');
    const acBackdrop = document.createElement('div');

    group.id = `${acGroupIdPrefix}${id}`;
    group.classList.add('group');
    group.setAttribute('data-expanded', 'false');

    elemContainer.id = `${acContainerIdPrefix}${id}`;
    elemContainer.className = 'off-canvas-autocomplete p-0 m-0 shadow-lg shadow-primary-dark border border-primary-dark';
    elemContainer.setAttribute('data-expanded', 'false');

    elem.setAttribute('autocomplete', 'off');
    elem.setAttribute('role', 'combobox');
    elem.setAttribute('aria-autocomplete', 'list');
    elem.setAttribute('aria-controls', autocompleteId);
    elem.setAttribute('aria-expanded', 'false');
    elem.classList.add('group');

    autocomplete.id = autocompleteId;
    autocomplete.tabIndex = 0;
    autocomplete.setAttribute('role', 'listbox');

    assistiveElement.id = "assistive" + autocompleteId;
    assistiveElement.className = "sr-only";
    assistiveElement.setAttribute('role', 'status');
    assistiveElement.setAttribute('aria-live', 'polite');
    assistiveElement.setAttribute('aria-atomic', 'true');
    assistiveElement.innerText = "When autocomplete results are available use up and down arrows to review and space or enter to select.  Touch device users, explore by touch or with swipe gestures.";

    acBackdrop.id = `${acBackdropIdPrefix}${id}`;
    acBackdrop.classList.add('autocomplete-backdrop');

    if (includeMobileHeader) {
        elemContainer.appendChild(getMobileHeader(elem, id, elem.getAttribute('data-autocomplete') ?? ""));
    }

    elemContainer.appendChild(autocomplete);
    elemContainer.appendChild(assistiveElement);
    group.appendChild(elemContainer);
    group.appendChild(acBackdrop);

    elem.after(group);

    autocomplete.addEventListener('click', (evt) => {
        elem.focus();
        autocomplete?.classList.remove('hidden');
    });

    autocompleteUIElements.push({ elem: elem, autocomplete: autocomplete, elemContainer: elemContainer });
    bindAutocompleteViewportListeners();
    updateAutocompleteUIElements();
}

function getMobileHeader(elem: HTMLInputElement, id: string, type: string): HTMLElement {
    const mobileHeader = document.createElement('div');
    const closeButton = document.createElement('button');
    const headerLabelContainer = document.createElement('div');
    const headerLabel = document.createElement('label');
    const headerInputContainer = document.createElement('div');
    const headerInput = document.createElement('input');
    const inputIcon = elem.id === 'k' ? createIcon(searchIconName) : elem.id === 'l' ? createIcon(locationIconName) : null;

    mobileHeader.className = "autocomplete-mobile-header p-2 border-b border-gray-light";

    headerInputContainer.className = "relative";

    headerInput.type = elem.type;
    headerInput.id = `${headerInputIdPrefix}${id}`;
    headerInput.className = "w-full";
    headerInput.setAttribute('autocomplete', 'off');
    headerInput.addEventListener('input', evt => {
        const state = getInteraction(elem);
        elem.value = headerInput.value;
        state.version++;
        autocompleteInput(evt, elem, id, state.version);
    });
    headerInput.addEventListener('keyup', evt => autocompleteKeyup(evt, elem, id));
    headerInput.addEventListener('search', () => { setAutocompleteDisplay(elem, id, false); });
    headerInput.addEventListener('keydown', evt => {
        if ((evt as KeyboardEvent).key === 'Enter') {
            evt.preventDefault();
            setAutocompleteDisplay(elem, id, false);
        }
    });
    headerInput.addEventListener('blur', () => autocompleteBlur(elem, id));

    headerLabelContainer.className = "flex items-center py-2";

    closeButton.type = 'button';
    closeButton.innerHTML = '<svg class="w-5 h-5 fill-gray-darkest mr-2" role="presentation"><use href="/img/icons/sprite.svg#arrow_back"></use></svg>';
    closeButton.addEventListener('click', () => setAutocompleteDisplay(elem, id, false));

    headerLabel.htmlFor = headerInput.id;
    headerLabel.className = 'font-bold';
    headerLabel.innerText =
        type === 'keyword' ? 'Enter a job or keyword to search' :
            type === 'location' && elem.type === 'search' ? 'Where do you want to work?' : `Enter a ${type}`;

    headerLabelContainer.appendChild(closeButton);
    headerLabelContainer.appendChild(headerLabel);

    if (inputIcon) {
        const iconSpan = document.createElement('span');
        iconSpan.className = "pointer-events-none absolute left-2 top-1/2 -translate-y-1/2";
        iconSpan.appendChild(inputIcon);
        headerInputContainer.appendChild(iconSpan);
        headerInput.classList.add('pl-8');
    }
    headerInputContainer.appendChild(headerInput);

    mobileHeader.appendChild(headerLabelContainer);
    mobileHeader.appendChild(headerInputContainer);

    return mobileHeader;
}

function resizeAutocomplete(elem: HTMLInputElement, acElem: HTMLElement) {
    if (window.innerWidth <= uiBreakpoints.extraSmall) {
        acElem.style.removeProperty('width');
    }
    else {
        acElem.style.width = (elem.getBoundingClientRect().width).toString() + "px";
    }
}

function isAutocompleteInteractionActive(elem: HTMLInputElement, id: string) {
    const activeElement = document.activeElement as HTMLElement | null;

    return activeElement === elem || !!document.querySelector(`#${acContainerIdPrefix}${id}`)?.contains(activeElement);
}

function populateAutocompleteList(elem: HTMLInputElement, data: any, id: string, acType: string = '') {
    const acElem = document.querySelector<HTMLElement>(`#${acElementIdPrefix}${id}`);

    if (!acElem) {
        return;
    }

    data = data ?? {};
    const previousState = autocompleteLists.find(item => item.id === id);
    if (previousState) {
        previousState.length = 0;
    }

    acElem.innerHTML = "";
    acElem.tabIndex = -1;

    const acItemCSS = "whitespace-pre flex items-center justify-between w-full cursor-pointer py-1 px-2 text-black font-bold hover:bg-primary-lighter focus:bg-primary-lighter";
    let currentPosition = 0;

        const heading = document.createElement('li');
        heading.className = 'capitalize flex items-center w-full py-1 px-2 text-gray';

    if (acType === "keyword" && elem.value.trim().length > 0) {
        const value = document.createElement('span');
        value.className = "font-bold text-black";
        value.textContent = elem.value;

        heading.append(createIcon(searchIconName), document.createTextNode('Search for "'), value, document.createTextNode('"'));
    } else {
        heading.textContent = Object.keys(data)[0] ?? '';
    }
    
    acElem.appendChild(heading);

    for (const property in data) {
        const results = (<AutocompleteResult[]>data[property]);

        if (results?.length > 0) {
            results.forEach(result => {
                if (result.Type === 'Series') {
                    result.Name = `${result.Code} - ${result.Name}`;
                }

                const listItem = document.createElement('li');
                ++currentPosition;

                listItem.tabIndex = -1;
                listItem.title = result.Name;
                listItem.setAttribute('aria-selected', 'false');
                listItem.setAttribute('role', 'option');
                listItem.setAttribute('aria-posinset', (currentPosition).toString());
                listItem.className = acItemCSS;

                const itemText = document.createElement('span');
                itemText.className = 'flex items-center';

                if (acType === 'location') {
                    itemText.appendChild(createIcon(locationIconName));
                } else if (acType === 'keyword') {
                    itemText.appendChild(createIcon(searchIconName));
                }

                appendFormattedACName(itemText, result.Name, elem.value);
                listItem.appendChild(itemText);
                listItem.id = `${acItemIdPrefix}${id}-${currentPosition}`;

                if (property === 'Recent') {
                    const removeButton = document.createElement('button');
                    removeButton.id = `${removeRecentIdPrefix}${id}-${currentPosition}`;
                    removeButton.type = 'button';
                    removeButton.innerHTML = closeIconHtml;
                    removeButton.className = "cursor-pointer";
                    removeButton.addEventListener('click', evt => {
                        evt.stopPropagation();
                        removeRecentSearch(result.Name, result.Type);
                        elem.value = '';
                        elem.focus();
                    });
                    listItem.appendChild(removeButton);
                }
                    acElem.appendChild(listItem);

                const detail: ACClickEventDetails = {
                    Type: acType,
                    Name: result.Name,
                    Code: result.Code,
                    ParamProperty: result.Type,
                }

                listItem.addEventListener('click', event => {
                    event.stopPropagation();
                    elem.value = result.Name;
                    setAutocompleteDisplay(elem, id, false);
                    document.dispatchEvent(new CustomEvent("acclick", { detail: detail }));
                });
                listItem.addEventListener('focusin', (evt) => {
                    if ((<HTMLElement>evt.target).id.startsWith(removeRecentIdPrefix)) {
                        return;
                    }
                    elem.value = result.Name;
                });
                listItem.addEventListener('blur', () => autocompleteBlur(elem, id));
                listItem.addEventListener('keydown', evt => autocompleteKeyup(evt, elem, id, detail));
            });
        }
    }

    if (currentPosition > 0) {
        const acListIndex = autocompleteLists.findIndex(i => i.id === id);
        if (acListIndex > -1) {
            autocompleteLists[acListIndex].length = currentPosition;
        } else {
            autocompleteLists.push({ id: id, length: currentPosition });
        }

        acElem.childNodes.forEach(node => {
            const self = (<HTMLElement>node);
            if (self.getAttribute('aria-posinset') !== null) {
                self.setAttribute('aria-setsize', currentPosition.toString())
            }
        });
    }
}

function setAutocompleteDisplay(elem: HTMLInputElement, id: string, isOpen: boolean) {
    const acElem = document.querySelector<HTMLDivElement>(`#${acContainerIdPrefix}${id}`);
    if (acElem === null) { return; }

    if (isOpen) {
        setMobileAutocompleteViewportPosition(acElem);
    } else {
        const state = getInteraction(elem);
        state.active = false;
        state.version++;
        displayLoadingIndicator(elem, id, false);
    }
    elem.setAttribute('aria-expanded', isOpen.toString());
    acElem.setAttribute('data-expanded', isOpen.toString());
}

function appendFormattedACName(parent: HTMLElement, acName: string, acTerm: string) {
    const term = acTerm.trim().toLowerCase();
    const termIndex = acName.toLowerCase().indexOf(term);

    if (termIndex < 0 || term.length === 0) {
        parent.append(document.createTextNode(acName));
        return;
    }

    parent.append(document.createTextNode(acName.substring(0, termIndex)));

    const matchedText = document.createElement('span');
    matchedText.className = 'font-normal';
    matchedText.textContent = acName.substring(termIndex, termIndex + term.length);
    parent.appendChild(matchedText);

    parent.append(document.createTextNode(acName.substring(termIndex + term.length)));
}

function createIcon(symbolID: string): SVGSVGElement {
    const svgNamespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNamespace, 'svg');
    const use = document.createElementNS(svgNamespace, 'use');

    svg.classList.add('w-4', 'h-4', 'fill-gray-darkest', 'mr-1');
    svg.setAttribute('role', 'presentation');
    use.setAttribute('href', `/img/icons/sprite.svg#${symbolID}`);

    svg.appendChild(use);
    return svg;
}

export function bindAutocompletes() {
    let config = getUsajConfig();
    DataHome = config.DataHome;
    BaseUrl = config.BaseUrl;

    document.querySelectorAll<HTMLInputElement>("[data-autocomplete]").forEach(elem => {
        const acEvent = elem.getAttribute('autocomplete-event');
        const id = getRandomId();
        const onfocus = elem.getAttribute('data-autocomplete-onfocus') || 'true';

        if (acEvent === null || acEvent !== 'true') {
            buildAutocompleteUI(elem, id);
            elem.setAttribute('autocomplete-event', 'true');
            const debouncedInput = debounce((evt: Event, version: number) => autocompleteInput(evt, elem, id, version), 300);
            elem.addEventListener('input', evt => {
                if (!getInteraction(elem).active && !beginInteraction(elem, id)) { return; }
                const state = getInteraction(elem);
                state.version++;
                debouncedInput(evt, state.version);
            });
            elem.addEventListener('keyup', evt => autocompleteKeyup(evt, elem, id));
            elem.addEventListener('blur', () => autocompleteBlur(elem, id));

            if (elem.id === 'k' || elem.id === 'l') {
                elem.addEventListener('focus', () => acSearchFocus(elem, id));
                elem.addEventListener('click', () => {
                    if (!getInteraction(elem).active) {
                        acSearchFocus(elem, id);
                    }
                });
            }
            else if (onfocus === 'true') {
                elem.addEventListener('focus', () => autocompleteFocus(elem, id));
                elem.addEventListener('click', () => {
                    if (!getInteraction(elem).active) {
                        autocompleteFocus(elem, id);
                    }
                });
            }
        }
    });
}
